import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getPayment,
  getPaymentRestriction,
} from "Store/akaAdvice/interfaces/getters";
import {
  AkaAdviceCheckboxes,
  AkaAdvicePriceFormValues,
} from "../../interfaces";

import { StyledAlert } from "App/global-style";
import CollapsibleContainer from "Components/CollapsibleContainer";
import { DetailsCardWrapper } from "Components/InsuranceDetailsCard/styled";
import { VALUE_TYPE } from "Components/NumberValue";
import { AdviceTextsDeathCommonChildren } from "Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/AdviceTexts/AdviceTextsDeathCommonChildren";
import { useAdviceDetailsFormValues } from "Features/CheckPrice/Products/Advice/ProductPrice/hooks/stateSelectors";
import { getPriceFormatFromValue } from "Formatting/index";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { useAkaAdviceBaseAmountSelector } from "Store/akaAdvice/akaAdviceSelectors";
import { isAkaGroupLifeDeathSelected } from "Store/akaAdvice/mappers/mapToCartProducts/mapToCartProducts";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { akaAdvicePriceCalculationRequestStart } from "Store/akaAdvice/slice";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useIsAkademikerneBranding } from "Store/partners/partnerHooks";
import { TextLanguage } from "Store/texts/interfaces";
import { FormikProps } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { AdviceCardComment } from "../../AdviceCardComment";
import { AdviceCardHeader } from "../../AdviceCardHeader";
import { CardTotalPrice } from "../../AdvicePrice/CardTotalPrice";
import { AdvisorUwInfo } from "../../AdvisorUwInfo";
import { AkaAdviceChartDeath } from "../../AkaAdviceCharts/AkaAdviceChartDeath";
import { Summary } from "../../Summary/Summary";
import { usePriceStatus } from "../../hooks/usePriceStatus";
import { ContentWrapper } from "../../styled";
import { AkaGroupLifeDeathCoInsuredForm } from "../Forms/AkaGroupLifeDeathCoInsuredForm";
import { AkaGroupLifeDeathForm } from "../Forms/AkaGroupLifeDeathForm";
import { DeathForm } from "../Forms/DeathForm";
import { AkaDeathAdviceText } from "../Texts/AkaDeathAdviceText";

const NEED_NAME = NeedName.LIFE;

export interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  adviceCard: any;
  lang: TextLanguage;
}

export const AkaDeathAdviceCard = ({
  formProps,
  need,
  adviceCard,
  lang,
}: Props): React.ReactElement | null => {
  const isAkaBranding = useIsAkademikerneBranding();
  const dispatch = useDispatch();
  const adviceDetails = useAdviceDetailsFormValues();
  const isAdvisor = useIsAdvisorSelector();
  const baseAmount = useAkaAdviceBaseAmountSelector();
  const selected = formProps.values.deathCheckbox;
  const currentSliderValue = formProps.values.deathInsuredSumSlider;
  const currentGValue = isAkaGroupLifeDeathSelected(formProps.values)
    ? Number(formProps.values.akaGroupLifeDeathGLevel ?? 0) * baseAmount
    : 0;
  const {
    priceIsChanging,
    setPriceIsChanging,
    priceIsLoading,
    setPriceIsLoading,
  } = usePriceStatus();
  const insuranceType: string = adviceCard.insuranceType[lang];

  const recalculatePrice = (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ): void => {
    setPriceIsChanging(false);
    setPriceIsLoading(true);
    dispatch(
      akaAdvicePriceCalculationRequestStart({
        isCollectiveAdvice: isAkaBranding,
        priceFormValues: formValues,
        specificProduct,
      }),
    );
  };

  if (!adviceDetails) {
    return null;
  }

  const adviceValueAkaGroupLifeDeath = getCorrectAkaAdviceValues(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH,
    isAdvisor,
  ).adviceValue;
  const effectivePaymentRateAkaGroupLifeDeath = getPayment(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH,
  ).effectivePaymentRate;
  const adviceValueDeath = getCorrectAkaAdviceValues(
    need,
    ProductCoverageName.DEATH,
    isAdvisor,
  ).adviceValue;
  const paymentRestriction = getPaymentRestriction(
    need,
    ProductCoverageName.DEATH,
  );

  const recommendedInsuranceSumAkaGroupLifeDeath = Math.trunc(
    adviceValueAkaGroupLifeDeath *
      baseAmount *
      effectivePaymentRateAkaGroupLifeDeath,
  );
  const recommendedInsuranceSum =
    recommendedInsuranceSumAkaGroupLifeDeath + adviceValueDeath;

  return (
    <div className="AdviceCard_DEATH">
      <DetailsCardWrapper
        key={insuranceType}
        adviceDetailsCard
        selected={selected}
      >
        <AdviceCardHeader
          insuranceId={AkaAdviceCheckboxes.DEATH_CHECKBOX}
          insuranceType={insuranceType}
          onChange={(e) => {
            const checked = e.target.checked;
            if (!checked) {
              formProps.setFieldValue(
                "akaGroupLifeDeathCoInsuredCheckbox",
                false,
              );
              formProps.setFieldValue(
                "incapacityOfWorkCoInsuredCheckbox",
                false,
              );
              formProps.setFieldValue(
                "timeLimitedDisabilityInterestCoInsuredCheckbox",
                false,
              );
            }
          }}
        />
        <ContentWrapper className="AdviceCardContent">
          <p className="AdviceHeaderText">
            Hvis du dør får dine etterlatte utbetalt et engangsbeløp, uansett
            dødsårsak.
          </p>
          <Summary
            text="Vi anbefaler en engangsutbetaling på"
            sum={recommendedInsuranceSum}
            valueType={VALUE_TYPE.NOK}
          />
          <AkaDeathAdviceText
            need={need}
            adviceDetails={adviceDetails}
            adviceTexts={{
              ...adviceCard.adviceTexts,
              adviceUnavailableMessages: adviceCard.adviceUnavailableMessages,
            }}
            lang={lang}
          />
          <CollapsibleContainer isOpen={selected}>
            <AkaAdviceChartDeath formProps={formProps} need={need} />

            <AdviceTextsDeathCommonChildren
              familySituation={adviceDetails.familySituation}
            />

            <AkaGroupLifeDeathForm
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              priceIsChanging={priceIsChanging}
              priceIsLoading={priceIsLoading}
              adviceCard={adviceCard}
              lang={lang}
            />

            {formProps.values.akaGroupLifeDeathCheckbox &&
              adviceDetails.addCoInsured === BooleanFormType.TRUE && (
                <AkaGroupLifeDeathCoInsuredForm
                  formProps={formProps}
                  recalculatePrice={recalculatePrice}
                  priceIsChanging={priceIsChanging}
                  priceIsLoading={priceIsLoading}
                  coInsuredBirthDate={adviceDetails.coInsuredBirthDate}
                />
              )}

            {adviceValueAkaGroupLifeDeath > 0 &&
              effectivePaymentRateAkaGroupLifeDeath < 1 && (
                <StyledAlert
                  className="EffectivePaymentInfo"
                  type="warning"
                  title={`A+ Gruppeliv livsforsikring nedtrappes fra 51 år og i beregningene har vi brukt din alder i dag. ${adviceValueAkaGroupLifeDeath} G vil gi en utbetaling på ${getPriceFormatFromValue(
                    recommendedInsuranceSumAkaGroupLifeDeath,
                    VALUE_TYPE.NOK,
                  )}`}
                />
              )}

            <DeathForm
              formProps={formProps}
              need={need}
              recalculatePrice={recalculatePrice}
              setPriceIsChanging={setPriceIsChanging}
              priceIsChanging={priceIsChanging}
              priceIsLoading={priceIsLoading}
              adviceCard={adviceCard}
              lang={lang}
            />

            {isAdvisor && formProps.values.deathInsuredSumCheckbox && (
              <AdvisorUwInfo
                insuranceName={INSURANCE_NAME_VALUE.DEATH}
                sliderValue={currentSliderValue ?? 0}
                paymentRestriction={paymentRestriction}
                uwInfoTexts={adviceCard.advisorTexts.uwInfoTexts}
                lang={lang}
                gValue={currentGValue}
              />
            )}
          </CollapsibleContainer>
          <AdviceCardComment
            commentId="deathAdviceComment"
            formProps={formProps}
            label="Notater om anbefaling og kundens valg av livsforsikring"
          />
          <CardTotalPrice
            formProps={formProps}
            needName={NEED_NAME}
            priceIsLoading={priceIsLoading}
          />
        </ContentWrapper>
      </DetailsCardWrapper>
    </div>
  );
};
